// multiPetStart.tsx - Responsive Version
import React, { useEffect, useState, useCallback } from 'react';
import { Check, Dog, Cat, PawPrint, X, FileText, CheckCircle, AlertTriangle, CreditCard, Clock } from 'lucide-react';
import { Pet } from '../types';
import LoadingSpinner from './common/LoadingSpinner';

interface MultiPetStartProps {
  isOpen: boolean;
  onClose: () => void;
  petCount?: number;
  onStartPet: (petIndex: number) => void;
  onComplete: () => void;
  petsData?: (Pet | null)[];
  completedPets?: Set<number>;
  draftedPets?: Map<number, { id: number; data: any }>;
  isRegistering?: boolean;
  onSaveDraft?: () => void;
  onFinishWithDrafts?: () => void;
  dashboardPets?: Pet[];
  completedPetIds?: Map<number, number>;
  onPayRegistrationFee?: (pet: Pet) => void;
}

const MultiPetStart: React.FC<MultiPetStartProps> = ({
  isOpen,
  onClose,
  petCount = 2,
  onStartPet,
  onComplete,
  petsData = [],
  completedPets = new Set(),
  draftedPets = new Map(),
  isRegistering = false,
  onSaveDraft,
  onFinishWithDrafts,
  dashboardPets = [],
  completedPetIds = new Map(),
  onPayRegistrationFee,
}) => {
  const [showConfirmClose, setShowConfirmClose] = useState(false);
  const [isSavingDraft, setIsSavingDraft] = useState(false);

  // Check if any registration has started
  const hasStartedAnyRegistration = petsData.some(pet => pet?.petName) || completedPets.size > 0;

  // Handle close with confirmation if needed
  const handleCloseAttempt = useCallback(() => {
    if (hasStartedAnyRegistration) {
      setShowConfirmClose(true);
    } else {
      onClose();
    }
  }, [hasStartedAnyRegistration, onClose]);

  const handleConfirmClose = () => {
    setShowConfirmClose(false);
    onClose();
  };

  const handleCancelClose = () => {
    setShowConfirmClose(false);
  };

  // Handle ESC key
  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen && !isRegistering) {
        if (showConfirmClose) {
          setShowConfirmClose(false);
        } else {
          handleCloseAttempt();
        }
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscKey);
    }

    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [isOpen, isRegistering, showConfirmClose, handleCloseAttempt]);

  // Reset confirmation state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setShowConfirmClose(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  // Helper function to format age as a user-friendly label
  const getAgeDisplay = (ageObj: any) => {
    if (!ageObj) return '';
    if (typeof ageObj === 'number') {
      const years = Math.floor(ageObj);
      return `${years} year${years === 1 ? '' : 's'}`;
    }
    const { years = 0, months = 0, weeks = 0, days = 0 } = ageObj || {};
    if (years && years > 0) return `${years} year${years === 1 ? '' : 's'}`;
    if (months && months > 0) return `${months} month${months === 1 ? '' : 's'}`;
    if (weeks && weeks > 0) return `${weeks} week${weeks === 1 ? '' : 's'}`;
    if (days && days > 0) return `${days} day${days === 1 ? '' : 's'}`;
    return '';
  };

  // Helper function to get animal type label
  const getAnimalTypeLabel = (animalType: string | undefined) => {
    if (animalType === 'esa') return 'Emotional Support Animal';
    if (animalType === 'sa') return 'Service Animal';
    return 'Pet';
  };

  // Create array of pets based on count
  const pets = Array.from({ length: petCount }, (_, index) => {
    const petIndex = index;
    const existingPetData = petsData[petIndex];
    const draftEntry = draftedPets.get(petIndex);
    const isCompleted = completedPets.has(petIndex);
    const isDrafted = !isCompleted && !!draftEntry;
    const displayData = isDrafted ? draftEntry.data : existingPetData;
    const animalType = displayData?.animalType || displayData?.pet_role || 'pet';
    
    return {
      id: petIndex + 1,
      index: petIndex,
      number: petIndex + 1,
      name: displayData?.petName || displayData?.pet_name || null,
      breed: displayData?.breed || null,
      ageDisplay: getAgeDisplay(displayData?.age),
      petType: displayData?.petType || displayData?.pet_type || null,
      animalType: animalType,
      animalTypeLabel: getAnimalTypeLabel(animalType),
      isCompleted: isCompleted,
      isDrafted: isDrafted,
      hasStarted: !!(existingPetData?.petName),
      data: displayData
    };
  });

  const handleStartPet = (petIndex: number) => {
    if (onStartPet) {
      onStartPet(petIndex);
    }
  };

  const handleComplete = () => {
    if (onComplete) {
      onComplete();
    }
  };

  const allPetsCompleted = pets.every(pet => pet.isCompleted);
  const draftedCount = pets.filter(pet => pet.isDrafted).length;
  const completedCount = pets.filter(pet => pet.isCompleted).length;
  const allAccountedFor = pets.every(pet => pet.isCompleted || pet.isDrafted);
  const progressPercentage = (completedCount / petCount) * 100;

  // Get pet icon based on type
  const getPetIcon = (petType: string) => {
    if (petType === 'cat') return Cat;
    if (petType === 'miniature_horse') return PawPrint;
    return Dog;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-0 sm:p-4">
      <div className="relative w-full h-full sm:h-auto max-w-6xl sm:max-h-[90vh]">
        {/* Outer container with gray background and border */}
        <div className="bg-gray-100 border-0 sm:border sm:border-gray-300 rounded-none sm:rounded-lg p-0 sm:p-2 shadow-none sm:shadow-2xl h-full">
          <div className="bg-white rounded-none sm:rounded-lg overflow-hidden h-full flex flex-col md:flex-row">
            
            {/* Loading Overlay */}
            {isRegistering && (
              <div className="absolute inset-0 bg-white bg-opacity-90 flex items-center justify-center z-50 rounded-none sm:rounded-lg">
                <LoadingSpinner size="large" text="Registering..." />
              </div>
            )}
            
            {/* Left Sidebar - Hidden on mobile */}
            <div className="hidden md:flex md:w-64 lg:w-80 bg-gray-50 p-4 lg:p-6 flex-col border-r border-gray-200">
              <div className="flex items-center gap-3 mb-4">
                <span className="font-semibold text-gray-900 text-lg">Add Animals</span>
              </div>
              
              <p className="text-sm text-gray-600 leading-relaxed mb-6">
                You've selected {petCount} {petCount === 1 ? 'animal' : 'animals'} to register. Complete each one at a time.
              </p>

              {/* Progress Section */}
              <div className="mt-auto">
                <div className="mb-3">
                  <div className="flex justify-between text-sm text-gray-600 mb-1">
                    <span>Progress</span>
                    <span className="font-medium">{completedCount} of {petCount} complete</span>
                  </div>
                  <div className="bg-gray-200 rounded-full h-2">
                    <div 
                      className="h-2 rounded-full transition-all duration-500 bg-[color:var(--pmc-primary)]"
                      style={{ width: `${progressPercentage}%` }}
                    />
                  </div>
                </div>

                {/* Status Summary */}
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                    <span className="text-gray-600">Completed: {completedCount}</span>
                  </div>
                  {draftedCount > 0 && (
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 bg-blue-400 rounded-full"></div>
                      <span className="text-gray-600">Drafted: {draftedCount}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-orange-400 rounded-full"></div>
                    <span className="text-gray-600">In Progress: {pets.filter(p => p.hasStarted && !p.isCompleted && !p.isDrafted).length}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-gray-300 rounded-full"></div>
                    <span className="text-gray-600">Not Started: {pets.filter(p => !p.hasStarted && !p.isCompleted && !p.isDrafted).length}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Main Content */}
            <div className="flex-1 flex flex-col min-h-0">
              {/* Header */}
              <div className="flex items-center justify-between px-4 sm:px-6 py-4 sm:py-5 border-b border-gray-200">
                <div className="flex-1 min-w-0">
                  <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-1">
                    Multi-Animal Registration
                  </h3>
                  <p className="text-xs sm:text-sm text-gray-600">
                    Select an animal below to {allPetsCompleted ? 'review or edit' : 'continue registration'}
                  </p>
                </div>
                {/* Close Button */}
                <button
                  onClick={handleCloseAttempt}
                  disabled={isRegistering}
                  className="flex-shrink-0 p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  aria-label="Close registration"
                >
                  <X className="w-5 h-5 sm:w-6 sm:h-6" />
                </button>
              </div>

              {/* Mobile Progress Bar */}
              <div className="md:hidden px-4 py-3 bg-gray-50 border-b border-gray-200">
                <div className="flex justify-between text-sm text-gray-600 mb-2">
                  <span className="font-medium">Progress</span>
                  <span>{completedCount} of {petCount} complete</span>
                </div>
                <div className="bg-gray-200 rounded-full h-2">
                  <div 
                    className="h-2 rounded-full transition-all duration-500 bg-[color:var(--pmc-primary)]"
                    style={{ width: `${progressPercentage}%` }}
                  />
                </div>
              </div>

              {/* Pet Cards */}
              <div className="flex-1 px-4 sm:px-6 py-4 sm:py-5 space-y-3 sm:space-y-4 overflow-y-auto">
                {pets.map((pet) => {
                  const isCompleted = pet.isCompleted;
                  const isDrafted = pet.isDrafted;
                  const hasStarted = pet.hasStarted && !isCompleted && !isDrafted;
                  const PetIcon = getPetIcon(pet.petType || 'dog');

                  const dashPetId = isCompleted ? completedPetIds?.get(pet.index) : undefined;
                  const dashPet = dashPetId ? dashboardPets?.find(p => (p.id || (p as any).pet_id) === dashPetId) : null;
                  const dashPetStatus = dashPet?.status;
                  const dashPaymentStatus = String((dashPet as any)?.stripe_payment_status || '').toLowerCase();
                  const dashPetRole = ((dashPet as any)?.pet_role || '').toLowerCase();
                  const isPendingPayment = dashPetStatus === 'PendingPayment';
                  const isPaymentFailed = isPendingPayment && dashPaymentStatus === 'failed';
                  const isFeeExempt = dashPetRole === 'esa' || dashPetRole === 'sa' || dashPaymentStatus === 'exempted';

                  return (
                    <div
                      key={pet.id}
                      className={`rounded-lg border-2 p-3 sm:p-4 transition-all ${
                        isCompleted 
                          ? (dashPet
                              ? (isPaymentFailed
                                  ? 'bg-red-50 border-red-300 hover:border-red-400'
                                  : isPendingPayment
                                    ? 'bg-amber-50 border-amber-300 hover:border-amber-400'
                                    : 'bg-green-50 border-green-300 hover:border-green-400')
                              : 'bg-green-50 border-green-300 hover:border-green-400')
                          : isDrafted
                            ? 'bg-blue-50 border-blue-300 hover:border-blue-400'
                            : hasStarted
                              ? 'bg-orange-50 border-orange-300 hover:border-orange-400'
                              : 'bg-white border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-center gap-3 sm:gap-4">
                        {/* Status Icon */}
                        <div className="flex-shrink-0">
                          {isCompleted ? (
                            <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center ${
                              dashPet
                                ? (isPaymentFailed
                                    ? 'bg-red-500'
                                    : isPendingPayment
                                      ? 'bg-amber-500'
                                      : 'bg-green-500')
                                : 'bg-green-500'
                            }`}>
                              {dashPet && isPaymentFailed ? (
                                <AlertTriangle className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                              ) : dashPet && isPendingPayment ? (
                                <Clock className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                              ) : (
                                <Check className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                              )}
                            </div>
                          ) : isDrafted ? (
                            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-blue-400 rounded-full flex items-center justify-center">
                              <FileText className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                            </div>
                          ) : hasStarted ? (
                            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-orange-400 rounded-full flex items-center justify-center">
                              <span className="text-white font-bold text-base sm:text-lg">{pet.number}</span>
                            </div>
                          ) : (
                            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gray-300 rounded-full flex items-center justify-center">
                              <span className="text-white font-bold text-base sm:text-lg">{pet.number}</span>
                            </div>
                          )}
                        </div>
                        
                        {/* Pet Info */}
                        <div className="flex-1 min-w-0">
                          <h4 className="text-sm sm:text-base font-semibold text-gray-900 mb-1 flex items-center gap-2">
                            {pet.animalTypeLabel} #{pet.number}
                            {pet.petType && <PetIcon className="w-4 h-4 text-gray-500 flex-shrink-0" />}
                          </h4>
                          <p className="text-xs sm:text-sm text-gray-600 truncate">
                            {isCompleted || isDrafted || hasStarted ? (
                              <span>
                                <span className="font-medium">{pet.name}</span>
                                {pet.ageDisplay && pet.breed && (
                                  <span className="hidden sm:inline"> &bull; {pet.ageDisplay} old {pet.breed}</span>
                                )}
                              </span>
                            ) : (
                              <span className="italic">No information added yet</span>
                            )}
                          </p>
                          {(isCompleted || isDrafted || hasStarted) && pet.ageDisplay && pet.breed && (
                            <p className="sm:hidden text-xs text-gray-500 mt-1 truncate">
                              {pet.ageDisplay} old {pet.breed}
                            </p>
                          )}
                          {isCompleted && (
                            dashPet ? (
                              <div className="mt-1 flex items-center gap-2 flex-wrap">
                                {isPaymentFailed ? (
                                  <span className="inline-flex items-center gap-1 text-xs text-red-600 font-medium">
                                    <AlertTriangle className="w-3 h-3" />
                                    Payment failed — please retry
                                  </span>
                                ) : isPendingPayment ? (
                                  <span className="inline-flex items-center gap-1 text-xs text-amber-600 font-medium">
                                    <Clock className="w-3 h-3" />
                                    Registered — awaiting payment
                                  </span>
                                ) : isFeeExempt ? (
                                  <span className="inline-flex items-center gap-1 text-xs text-green-600 font-medium">
                                    <CheckCircle className="w-3 h-3" />
                                    Registered — fee exempt
                                  </span>
                                ) : dashPetStatus === 'Active' ? (
                                  <span className="inline-flex items-center gap-1 text-xs text-green-600 font-medium">
                                    <CheckCircle className="w-3 h-3" />
                                    Active
                                  </span>
                                ) : dashPetStatus === 'Pending' ? (
                                  <span className="inline-flex items-center gap-1 text-xs text-orange-600 font-medium">
                                    <Clock className="w-3 h-3" />
                                    Pending review
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center gap-1 text-xs text-green-600 font-medium">
                                    <Check className="w-3 h-3" />
                                    {dashPetStatus || 'Registered'}
                                  </span>
                                )}
                              </div>
                            ) : (
                              <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                                <Check className="w-3 h-3" />
                                Registration complete
                              </p>
                            )
                          )}
                          {isDrafted && (
                            <p className="text-xs text-blue-600 mt-1 flex items-center gap-1">
                              <FileText className="w-3 h-3" />
                              Saved as draft
                            </p>
                          )}
                          {hasStarted && (
                            <p className="text-xs text-orange-600 mt-1">
                              Registration in progress...
                            </p>
                          )}
                        </div>

                        {/* Action Button */}
                        {isCompleted && dashPet && isPendingPayment && onPayRegistrationFee ? (
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                onPayRegistrationFee(dashPet);
                              }}
                              className={`px-3 sm:px-4 py-2 sm:py-2.5 rounded-full text-xs sm:text-sm font-bold uppercase tracking-wide transition-all flex items-center gap-1.5 ${
                                isPaymentFailed
                                  ? 'bg-red-600 text-white hover:bg-red-700'
                                  : 'bg-[color:var(--pmc-primary)] text-white hover:bg-[color:var(--pmc-primary)]'
                              }`}
                            >
                              <CreditCard className="w-3.5 h-3.5" />
                              {isPaymentFailed ? 'Retry' : 'Pay'}
                            </button>
                            <button
                              onClick={() => handleStartPet(pet.index)}
                              className="px-3 sm:px-4 py-2 sm:py-2.5 rounded-full text-xs sm:text-sm font-bold uppercase tracking-wide transition-all bg-white border-2 border-gray-300 text-gray-700 hover:bg-gray-50"
                            >
                              Edit
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => handleStartPet(pet.index)}
                            className={`flex-shrink-0 px-4 sm:px-5 py-2 sm:py-2.5 rounded-full text-xs sm:text-sm font-bold uppercase tracking-wide transition-all ${
                              isCompleted
                                ? 'bg-white border-2 border-gray-300 text-gray-700 hover:bg-gray-50'
                                : isDrafted
                                  ? 'bg-[color:var(--pmc-primary)] text-white hover:bg-[color:var(--pmc-primary)] hover:shadow-lg'
                                  : hasStarted
                                    ? 'bg-orange-500 text-white hover:bg-orange-600 hover:shadow-lg'
                                    : 'bg-[color:var(--pmc-primary)] text-white hover:shadow-lg transform hover:scale-105'
                            }`}
                          >
                            {isCompleted ? 'Edit' : isDrafted ? 'Resume' : hasStarted ? 'Continue' : 'Start'}
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Footer */}
              <div className="px-4 sm:px-6 py-4 sm:py-5 border-t border-gray-200 bg-gray-50">
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
                  <div className="text-xs sm:text-sm text-gray-600 text-center sm:text-left">
                    {allPetsCompleted ? (
                      <span className="flex items-center justify-center sm:justify-start gap-2 text-green-600 font-medium">
                        <Check className="w-4 h-4" />
                        All registrations complete!
                      </span>
                    ) : allAccountedFor && draftedCount > 0 ? (
                      <span className="flex items-center justify-center sm:justify-start gap-2 text-blue-600 font-medium">
                        <FileText className="w-4 h-4" />
                        {completedCount} registered, {draftedCount} saved as draft
                      </span>
                    ) : (
                      <span>Complete all registrations to proceed</span>
                    )}
                  </div>
                  <div className="flex flex-col sm:flex-row gap-2">
                    {allAccountedFor && draftedCount > 0 && !allPetsCompleted && onFinishWithDrafts && (
                      <button
                        onClick={onFinishWithDrafts}
                        className="w-full sm:w-auto px-6 py-2.5 rounded-full text-xs sm:text-sm font-bold uppercase tracking-wide transition-all bg-white border-2 border-blue-400 text-blue-600 hover:bg-blue-50"
                      >
                        Save &amp; Exit
                      </button>
                    )}
                    <button
                      onClick={handleComplete}
                      disabled={!allPetsCompleted}
                      className={`w-full sm:w-auto px-6 py-2.5 rounded-full text-xs sm:text-sm font-bold uppercase tracking-wide transition-all ${
                        allPetsCompleted
                          ? 'bg-[color:var(--pmc-primary)] text-white hover:shadow-lg transform hover:scale-105'
                          : 'bg-gray-300 text-gray-500 cursor-not-allowed opacity-60'
                      }`}
                    >
                      {allPetsCompleted ? 'Complete All' : `Complete ${petCount - completedCount} More`}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Confirmation Modal */}
        {showConfirmClose && (
          <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center z-60 rounded-none sm:rounded-lg">
            <div className="bg-white rounded-lg p-6 mx-4 max-w-md w-full shadow-xl">
              <h4 className="text-lg font-semibold text-gray-900 mb-2">
                Save your progress?
              </h4>
              <p className="text-sm text-gray-600 mb-6">
                Would you like to save your progress as a draft? You can resume later from where you left off.
              </p>
              <div className="flex flex-col gap-2">
                {onSaveDraft && (
                  <button
                    onClick={async () => {
                      setIsSavingDraft(true);
                      try {
                        await onSaveDraft();
                        setShowConfirmClose(false);
                      } finally {
                        setIsSavingDraft(false);
                      }
                    }}
                    disabled={isSavingDraft}
                    className="w-full px-4 py-2.5 bg-[color:var(--pmc-primary)] text-white rounded-full font-medium hover:shadow-lg transition-all disabled:opacity-60"
                  >
                    {isSavingDraft ? 'Saving...' : 'Save as Draft'}
                  </button>
                )}
                <button
                  onClick={handleCancelClose}
                  className="w-full px-4 py-2.5 border border-gray-300 text-gray-700 rounded-full font-medium hover:bg-gray-50 transition-colors"
                >
                  Continue Registration
                </button>
                <button
                  onClick={handleConfirmClose}
                  className="w-full px-4 py-2 text-sm text-red-600 font-medium hover:text-red-700 transition-colors"
                >
                  Discard Progress
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MultiPetStart;